import { Hero } from '@/components/Hero'
import { MemberSpotlightArticle } from '@/components/MemberSpotlightArticle/MemberSpotlightArticle'
import { MemberSpotlightQuery } from '@/components/MemberSpotlightArticle/MemberSpotlightArticle.query'
import {
  MemberSpotlightArticleRequest,
  MemberSpotlightArticle as MemberSpotlightArticleType,
} from '@/components/MemberSpotlightArticle/MemberSpotlightArticle.types'
import { SubmitYourRideCallout } from '@/components/MemberSpotlightArticle/SubmitYourRideCallout'
import { MemberSpotlightArticleList } from '@/components/MemberSpotlightArticleList'
import { statamicGraphqlEndpoint } from '@/globals/globals'
import { getPartImage } from '@/lib/getPartImage'
import { Part } from '@/types/part'
import { PartGroup } from '@/types/partGroup'
import request, { gql } from 'graphql-request'
import { isArray, uniqueId } from 'lodash'
import { GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'
import Image from 'next/image'

export async function getStaticPaths() {
  const { data: { projects } = { projects: [] } } = await request(
    statamicGraphqlEndpoint,
    gql`
      query MyQuery {
        entries(collection: "member-spotlight-articles") {
          total
          data {
            ... on Entry_MemberSpotlightArticles_MemberSpotlightArticles {
              slug
            }
          }
        }
      }
    `
  )

  // check if we want to have a faster build
  const fullBuild = process.env.NEXT_BUILD_TYPE !== 'fast'

  // Build the params from the response object
  const paths = fullBuild
    ? projects.map((project: any) => {
        return {
          params: {
            slug: project.slug,
          },
        }
      })
    : []

  return { paths, fallback: true }
}

export const getStaticProps: GetStaticProps = async context => {
  const { entry } = await request<MemberSpotlightArticleRequest>(
    statamicGraphqlEndpoint,
    MemberSpotlightQuery,
    {
      slug: context.params?.slug,
    }
  )

  const { entries: articlesResponse } = await request<{
    entries: { data: Array<MemberSpotlightArticleType> }
  }>(
    statamicGraphqlEndpoint,
    `query MyQuery {
        entries(collection: "member-spotlight-articles") {
          data {
            ... on Entry_MemberSpotlightArticles_MemberSpotlightArticles {
              slug
              title
              hero_image {
                ... on Asset_Main {
                  id
                  url
                  width
                  alt
                  height
                }
              }
            }
          }
        }
      }`
  )

  // Remove entries that match current slug
  const otherArticles = isArray(articlesResponse.data)
    ? articlesResponse.data?.filter(({ slug }) => slug != entry.slug)
    : null

  const seoTitle = entry?.title
    ? `${entry?.title} - Spotlight`
    : 'Spotlight article'

  const partsList = entry?.parts_list?.map(
    (group): PartGroup => ({
      type:
        group.partType.label === 'Uncategorized' ? null : group.partType.value,
      id: uniqueId(),
      icon: getPartImage(group.partType.value),
      name:
        group.partType.label === 'Uncategorized' ? null : group.partType.label,
      parts: group.parts?.map(
        (part): Part => ({
          id: uniqueId(),
          name: part.name,
          number: part.number,
          productUrl: part.product_url,
          type: group.partType.value,
        })
      ),
    })
  )

  delete entry?.parts_list

  return {
    props: {
      entry,
      otherArticles,
      partsList,
      seoTitle,
    },
    revalidate: 15,
  }
}

type PageProps = {
  entry: MemberSpotlightArticleType
  otherArticles: Array<Partial<MemberSpotlightArticleType>>
  partsList: PartGroup[]
  seoTitle: string
}

export const Page = (props: PageProps) => {
  return (
    <>
      <NextSeo title={props.seoTitle} />
      <Hero
        label='Member Spotlight'
        heading={props.entry?.title}
        subheading={props.entry?.intro}>
        {props.entry?.hero_image?.url ? (
          <Image
            alt={props.entry.hero_image.alt ?? ''}
            height={props.entry.hero_image.height}
            src={props.entry.hero_image.url}
            width={props.entry.hero_image.width}
            style={{
              marginInline: 'auto',
              width: '100%',
              maxWidth: '900px',
              marginTop: '80px',
            }}
          />
        ) : (
          <></>
        )}
      </Hero>
      <MemberSpotlightArticle entry={props.entry} partsList={props.partsList} />
      <SubmitYourRideCallout />
      <MemberSpotlightArticleList
        articles={props.otherArticles}
        heading='More Spotlight Builds'
      />
    </>
  )
}

export default Page
